import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Statistic, Row, Col, Tag, Space, Typography, Divider, Switch, List, Collapse, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    DollarOutlined,
    UserOutlined,
    ShoppingOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    TableOutlined,
    AppstoreOutlined,
    SearchOutlined,
    UpOutlined,
    DownOutlined
} from '@ant-design/icons';
import orderData from './data.json';
import type { OrderUser, OrderProduct, ProductQuantityStat, OverviewStats } from '../../types';
import styles from './index.module.css';

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { Search } = Input;

const Goods: React.FC = () => {
    const [data, setData] = useState<OrderUser[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [viewMode, setViewMode] = useState<'table' | 'card'>(window.innerWidth >= 768 ? 'table' : 'card');
    const [searchKeyword, setSearchKeyword] = useState<string>('');
    const [productStatsVisible, setProductStatsVisible] = useState<boolean>(false);

    useEffect(() => {
        // 模拟加载数据
        setLoading(true);
        setTimeout(() => {
            setData(orderData as OrderUser[]);
            setLoading(false);
        }, 100);
    }, []);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            // 如果切换到移动端且当前是表格模式，自动切换到卡片模式
            // 如果切换到桌面端且当前是卡片模式，自动切换到表格模式
            if (mobile && viewMode === 'table') {
                setViewMode('card');
            } else if (!mobile && viewMode === 'card') {
                setViewMode('table');
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [viewMode]);

    // 根据搜索关键词过滤数据（同时覆盖用户和商品维度）
    const filteredData = useMemo<OrderUser[]>(() => {
        if (!searchKeyword.trim()) {
            return data;
        }

        const keyword = searchKeyword.trim().toLowerCase();
        return data
            .map(user => {
                // 检查用户昵称是否匹配
                const userMatch = user.userNickname.toLowerCase().includes(keyword);
                
                // 检查商品名称是否匹配
                const filteredProducts = user.products.filter(product =>
                    product.productName.toLowerCase().includes(keyword) ||
                    (product.baseProductName && product.baseProductName.toLowerCase().includes(keyword))
                );

                // 如果用户昵称匹配或商品匹配，则包含该用户
                if (userMatch || filteredProducts.length > 0) {
                    return {
                        ...user,
                        products: userMatch ? user.products : filteredProducts
                    };
                }
                return null;
            })
            .filter((item): item is OrderUser => item !== null);
    }, [data, searchKeyword]);

    // 计算商品数量统计（基于过滤后的数据）
    const productQuantityStats = useMemo<ProductQuantityStat[]>(() => {
        const productMap: Record<string, { productName: string; totalQuantity: number; userCount: number; users: Set<string> }> = {};
        
        filteredData.forEach(user => {
            user.products.forEach(product => {
                const baseName = product.baseProductName || product.productName.replace(/\*\d+/g, '').trim();
                const quantity = product.quantity || 1;
                
                if (!productMap[baseName]) {
                    productMap[baseName] = {
                        productName: baseName,
                        totalQuantity: 0,
                        userCount: 0,
                        users: new Set()
                    };
                }
                
                productMap[baseName].totalQuantity += quantity;
                productMap[baseName].users.add(user.userNickname);
                productMap[baseName].userCount = productMap[baseName].users.size;
            });
        });
        
        return Object.values(productMap)
            .map(item => ({
                ...item,
                users: Array.from(item.users)
            }))
            .sort((a, b) => b.totalQuantity - a.totalQuantity);
    }, [filteredData]);

    // 高亮显示匹配的文本
    const highlightText = (text: string, keyword: string): React.ReactNode => {
        if (!keyword.trim() || !text) {
            return text;
        }

        const parts = text.split(new RegExp(`(${keyword})`, 'gi'));
        return parts.map((part, index) =>
            part.toLowerCase() === keyword.toLowerCase() ? (
                <span key={index} style={{ backgroundColor: '#fff566', fontWeight: 'bold' }}>
                    {part}
                </span>
            ) : (
                part
            )
        );
    };

    // 计算总览统计（基于过滤后的数据）
    const overviewStats = useMemo<OverviewStats>(() => {
        const totalUsers = filteredData.length;
        const totalProductTypes = filteredData.reduce((sum, user) => sum + (user.productTypeCount || user.products.length), 0);
        const totalProductQuantity = filteredData.reduce((sum, user) => sum + (user.productQuantity || user.products.reduce((pSum, p) => pSum + (p.quantity || 1), 0)), 0);
        const totalPaidPrice65 = filteredData.reduce((sum, user) => sum + user.totalPaidPrice65, 0);
        const totalUnpaidPrice65 = filteredData.reduce((sum, user) => sum + user.totalUnpaidPrice65, 0);
        const totalPrice65 = totalPaidPrice65 + totalUnpaidPrice65;
        const totalPaidPrice78 = filteredData.reduce((sum, user) => sum + user.totalPaidPrice78, 0);
        const totalUnpaidPrice78 = filteredData.reduce((sum, user) => sum + user.totalUnpaidPrice78, 0);
        const totalPrice78 = totalPaidPrice78 + totalUnpaidPrice78;
        const paidUsers65 = filteredData.filter(user => user.totalPaidPrice65 > 0).length;
        const unpaidUsers65 = filteredData.filter(user => user.totalUnpaidPrice65 > 0).length;
        const paidUsers78 = filteredData.filter(user => user.totalPaidPrice78 > 0).length;
        const unpaidUsers78 = filteredData.filter(user => user.totalUnpaidPrice78 > 0).length;

        return {
            totalUsers,
            totalProductTypes,
            totalProductQuantity,
            totalPaidPrice65,
            totalUnpaidPrice65,
            totalPrice65,
            totalPaidPrice78,
            totalUnpaidPrice78,
            totalPrice78,
            paidUsers65,
            unpaidUsers65,
            paidUsers78,
            unpaidUsers78
        };
    }, [filteredData]);

    // 用户表格列定义
    const userColumns: ColumnsType<OrderUser> = [
        {
            title: '用户昵称',
            dataIndex: 'userNickname',
            key: 'userNickname',
            width: 150,
            fixed: 'left',
            render: (text) => highlightText(text, searchKeyword),
        },
        {
            title: '商品种类',
            dataIndex: 'productTypeCount',
            key: 'productTypeCount',
            width: 100,
            align: 'center',
            render: (count, record) => count || record.products.length,
        },
        {
            title: '实际数量',
            dataIndex: 'productQuantity',
            key: 'productQuantity',
            width: 100,
            align: 'center',
            render: (quantity, record) => {
                // 如果没有productQuantity字段，则计算products中所有商品的数量总和
                if (quantity) return quantity;
                return record.products.reduce((sum, p) => sum + (p.quantity || 1), 0);
            },
        },
        {
            title: '已付款（65折）',
            dataIndex: 'totalPaidPrice65',
            key: 'totalPaidPrice65',
            width: 140,
            align: 'right',
            render: (price) => `¥${price.toFixed(2)}`,
            sorter: (a, b) => a.totalPaidPrice65 - b.totalPaidPrice65,
        },
        {
            title: '未付款（65折）',
            dataIndex: 'totalUnpaidPrice65',
            key: 'totalUnpaidPrice65',
            width: 140,
            align: 'right',
            render: (price) => `¥${price.toFixed(2)}`,
            sorter: (a, b) => a.totalUnpaidPrice65 - b.totalUnpaidPrice65,
        },
        {
            title: '总价（65折）',
            dataIndex: 'totalPrice65',
            key: 'totalPrice65',
            width: 140,
            align: 'right',
            render: (price) => (
                <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
                    ¥{price.toFixed(2)}
                </span>
            ),
            sorter: (a, b) => a.totalPrice65 - b.totalPrice65,
            defaultSortOrder: 'descend',
        },
        {
            title: '总价（78折）',
            dataIndex: 'totalPrice78',
            key: 'totalPrice78',
            width: 140,
            align: 'right',
            render: (price) => (
                <span style={{ fontWeight: 'bold', color: '#722ed1' }}>
                    ¥{price.toFixed(2)}
                </span>
            ),
            sorter: (a, b) => a.totalPrice78 - b.totalPrice78,
        },
        {
            title: '65折付款状态',
            key: 'paymentStatus65',
            width: 120,
            render: (_, record) => {
                if (record.totalPaidPrice65 > 0 && record.totalUnpaidPrice65 > 0) {
                    return <Tag color="orange">部分付款</Tag>;
                } else if (record.totalPaidPrice65 > 0) {
                    return <Tag color="green">已付款</Tag>;
                } else {
                    return <Tag color="default">未付款</Tag>;
                }
            },
        },
        {
            title: '78折补肾情况',
            key: 'paymentStatus78',
            width: 120,
            render: (_, record) => {
                if (record.totalPaidPrice78 > 0 && record.totalUnpaidPrice78 > 0) {
                    return <Tag color="orange">部分付款</Tag>;
                } else if (record.totalPaidPrice78 > 0) {
                    return <Tag color="green">已付款</Tag>;
                } else {
                    return <Tag color="default">未付款</Tag>;
                }
            },
        },
    ];

    // 展开行：显示用户的所有商品
    const expandedRowRender = (record: OrderUser) => {
        const productColumns: ColumnsType<OrderProduct> = [
            {
                title: '商品名称',
                dataIndex: 'productName',
                key: 'productName',
                width: 200,
                render: (text) => highlightText(text, searchKeyword),
            },
            {
                title: '原价',
                dataIndex: 'originalPrice',
                key: 'originalPrice',
                width: 100,
                align: 'right',
                render: (price) => `¥${price.toFixed(2)}`,
            },
            {
                title: '65折价格',
                dataIndex: 'price65',
                key: 'price65',
                width: 120,
                align: 'right',
                render: (price) => (
                    <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
                        ¥{price.toFixed(2)}
                    </span>
                ),
            },
            {
                title: '78折价格',
                dataIndex: 'price78',
                key: 'price78',
                width: 120,
                align: 'right',
                render: (price) => (
                    <span style={{ fontWeight: 'bold', color: '#722ed1' }}>
                        ¥{price.toFixed(2)}
                    </span>
                ),
            },
            {
                title: '65折付款',
                dataIndex: 'isPaid65',
                key: 'isPaid65',
                width: 100,
                align: 'center',
                render: (isPaid65) => (
                    isPaid65 ? (
                        <Tag color="green" icon={<CheckCircleOutlined />}>已付款</Tag>
                    ) : (
                        <Tag color="default" icon={<ClockCircleOutlined />}>未付款</Tag>
                    )
                ),
            },
            {
                title: '78折付款',
                dataIndex: 'isPaid78',
                key: 'isPaid78',
                width: 100,
                align: 'center',
                render: (isPaid78) => (
                    isPaid78 ? (
                        <Tag color="green" icon={<CheckCircleOutlined />}>已付款</Tag>
                    ) : (
                        <Tag color="default" icon={<ClockCircleOutlined />}>未付款</Tag>
                    )
                ),
            },
            {
                title: '是否补肾',
                dataIndex: 'isNeedSupplement',
                key: 'isNeedSupplement',
                width: 100,
                align: 'center',
                render: (isNeedSupplement) => (
                    isNeedSupplement ? (
                        <Tag color="red">是</Tag>
                    ) : (
                        <Tag color="default">否</Tag>
                    )
                ),
            },
            {
                title: '备注',
                dataIndex: 'remark',
                key: 'remark',
                width: 200,
                render: (remark) => (
                    remark ? (
                        <Text type="secondary" style={{ fontSize: '12px' }}>{remark}</Text>
                    ) : (
                        <Text type="secondary" style={{ fontSize: '12px', color: '#d9d9d9' }}>-</Text>
                    )
                ),
            },
        ];

        return (
            <Table
                columns={productColumns}
                dataSource={record.products}
                rowKey={(record, index) => `${record.productName}-${index}`}
                pagination={false}
            />
        );
    };

    // 获取付款状态标签（显示65折和78折的付款状态）
    const getPaymentStatusTag = (record: OrderUser) => {
        const paid65 = record.totalPaidPrice65 > 0;
        const unpaid65 = record.totalUnpaidPrice65 > 0;
        const paid78 = record.totalPaidPrice78 > 0;
        const unpaid78 = record.totalUnpaidPrice78 > 0;
        
        if (paid65 && unpaid65) {
            return <Tag color="orange">65折部分付款</Tag>;
        } else if (paid65) {
            return <Tag color="green">65折已付款</Tag>;
        } else if (paid78 && unpaid78) {
            return <Tag color="orange">78折部分付款</Tag>;
        } else if (paid78) {
            return <Tag color="blue">78折已付款</Tag>;
        } else {
            return <Tag color="default">未付款</Tag>;
        }
    };

    // 卡片模式渲染
    const renderCardView = () => {
        return (
            <Row gutter={[16, 16]}>
                {filteredData.map((user) => (
                    <Col xs={24} sm={12} lg={8} xl={6} key={user.userNickname}>
                        <Card
                            className={styles.userCard}
                            hoverable
                            title={
                                <Space>
                                    <UserOutlined />
                                    <Text strong>{highlightText(user.userNickname, searchKeyword)}</Text>
                                </Space>
                            }
                            extra={getPaymentStatusTag(user)}
                        >
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                <Row gutter={8}>
                                    <Col span={12}>
                                        <div className={styles.cardStat}>
                                            <Text type="secondary">商品数量</Text>
                                            <Text strong style={{ fontSize: '18px' }}>
                                                {user.products.length}
                                            </Text>
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div className={styles.cardStat}>
                                            <Text type="secondary">总价（65折）</Text>
                                            <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
                                                ¥{user.totalPrice65.toFixed(2)}
                                            </Text>
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div className={styles.cardStat}>
                                            <Text type="secondary">总价（78折）</Text>
                                            <Text strong style={{ fontSize: '18px', color: '#722ed1' }}>
                                                ¥{user.totalPrice78.toFixed(2)}
                                            </Text>
                                        </div>
                                    </Col>
                                </Row>
                                <Divider style={{ margin: '12px 0' }} />
                                <Row gutter={8}>
                                    <Col span={12}>
                                        <div className={styles.cardStat}>
                                            <Text type="secondary">已付款（65折）</Text>
                                            <Text style={{ color: '#52c41a' }}>
                                                ¥{user.totalPaidPrice65.toFixed(2)}
                                            </Text>
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div className={styles.cardStat}>
                                            <Text type="secondary">未付款（65折）</Text>
                                            <Text style={{ color: '#fa8c16' }}>
                                                ¥{user.totalUnpaidPrice65.toFixed(2)}
                                            </Text>
                                        </div>
                                    </Col>
                                </Row>
                                <Collapse ghost>
                                    <Panel header="查看商品明细" key="1">
                                        <List
                                            dataSource={user.products}
                                            renderItem={(product) => (
                                                <List.Item className={styles.productItem}>
                                                    <Space direction="vertical" style={{ width: '100%' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Text>{highlightText(product.productName, searchKeyword)}</Text>
                                                            <Space>
                                                                {product.isNeedSupplement && (
                                                                    <Tag color="red">补肾</Tag>
                                                                )}
                                                                {product.isPaid65 ? (
                                                                    <Tag color="green" icon={<CheckCircleOutlined />}>65折已付</Tag>
                                                                ) : (
                                                                    <Tag color="default" icon={<ClockCircleOutlined />}>65折未付</Tag>
                                                                )}
                                                                {product.isPaid78 ? (
                                                                    <Tag color="blue" icon={<CheckCircleOutlined />}>78折已付</Tag>
                                                                ) : (
                                                                    <Tag color="default" icon={<ClockCircleOutlined />}>78折未付</Tag>
                                                                )}
                                                            </Space>
                                                        </div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginTop: '4px' }}>
                                                            <Space>
                                                                <Text type="secondary">原价: ¥{product.originalPrice.toFixed(2)}</Text>
                                                                {(product.quantity && product.quantity > 1) && (
                                                                    <Tag color="blue">×{product.quantity}</Tag>
                                                                )}
                                                            </Space>
                                                            <Space>
                                                                <Text strong style={{ color: '#1890ff' }}>
                                                                    65折: ¥{product.price65.toFixed(2)}
                                                                </Text>
                                                                <Text strong style={{ color: '#722ed1' }}>
                                                                    78折: ¥{product.price78.toFixed(2)}
                                                                </Text>
                                                            </Space>
                                                        </div>
                                                        {product.remark && (
                                                            <div style={{ marginTop: '4px', fontSize: '11px' }}>
                                                                <Text type="secondary" style={{ fontStyle: 'italic' }}>
                                                                    备注: {product.remark}
                                                                </Text>
                                                            </div>
                                                        )}
                                                    </Space>
                                                </List.Item>
                                            )}
                                        />
                                    </Panel>
                                </Collapse>
                            </Space>
                        </Card>
                    </Col>
                ))}
            </Row>
        );
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <Title level={2} className={styles.title}>绝区零 FES 普谷</Title>
            </div>

            {/* 搜索框 */}
            <Card className={styles.searchCard}>
                <Search
                    placeholder="搜索用户昵称或商品名称..."
                    allowClear
                    enterButton={<SearchOutlined />}
                    size="large"
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    onSearch={(value) => setSearchKeyword(value)}
                    className={styles.searchInput}
                />
            </Card>

            {/* 总览统计 */}
            <Row gutter={[16, 16]} className={styles.overviewRow}>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="总用户数"
                            value={overviewStats.totalUsers}
                            prefix={<UserOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="商品种类数"
                            value={overviewStats.totalProductTypes}
                            prefix={<ShoppingOutlined />}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="实际商品数量"
                            value={overviewStats.totalProductQuantity}
                            prefix={<ShoppingOutlined />}
                            valueStyle={{ color: '#eb2f96' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="已付款总额（65折）"
                            value={overviewStats.totalPaidPrice65}
                            prefix={<DollarOutlined />}
                            precision={2}
                            suffix="元"
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
            </Row>
            <Row gutter={[16, 16]} className={styles.overviewRow}>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="未付款总额（65折）"
                            value={overviewStats.totalUnpaidPrice65}
                            prefix={<DollarOutlined />}
                            precision={2}
                            suffix="元"
                            valueStyle={{ color: '#fa8c16' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="已付款总额（78折）"
                            value={overviewStats.totalPaidPrice78}
                            prefix={<DollarOutlined />}
                            precision={2}
                            suffix="元"
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="未付款总额（78折）"
                            value={overviewStats.totalUnpaidPrice78}
                            prefix={<DollarOutlined />}
                            precision={2}
                            suffix="元"
                            valueStyle={{ color: '#fa8c16' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="总计（78折）"
                            value={overviewStats.totalPrice78}
                            prefix={<DollarOutlined />}
                            precision={2}
                            suffix="元"
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* 78折统计 */}
            <Row gutter={[16, 16]} className={styles.overviewRow}>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="已付款总额（78折）"
                            value={overviewStats.totalPaidPrice78}
                            prefix={<DollarOutlined />}
                            precision={2}
                            suffix="元"
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="未付款总额（78折）"
                            value={overviewStats.totalUnpaidPrice78}
                            prefix={<DollarOutlined />}
                            precision={2}
                            suffix="元"
                            valueStyle={{ color: '#fa8c16' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="总计（78折）"
                            value={overviewStats.totalPrice78}
                            prefix={<DollarOutlined />}
                            precision={2}
                            suffix="元"
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* 汇总信息 */}
            <Card className={styles.summaryCard}>
                <Row gutter={[24, 16]}>
                    <Col xs={24} sm={12} md={6}>
                        <Space direction="vertical">
                            <span className={styles.summaryLabel}>总计（65折）：</span>
                            <span className={styles.summaryValue}>
                                ¥{overviewStats.totalPrice65.toFixed(2)}
                            </span>
                        </Space>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Space direction="vertical">
                            <span className={styles.summaryLabel}>总计（78折）：</span>
                            <span className={styles.summaryValue}>
                                ¥{overviewStats.totalPrice78.toFixed(2)}
                            </span>
                        </Space>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Space direction="vertical">
                            <span className={styles.summaryLabel}>65折已付款用户：</span>
                            <span className={styles.summaryValue}>
                                {overviewStats.paidUsers65} 人
                            </span>
                        </Space>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Space direction="vertical">
                            <span className={styles.summaryLabel}>78折已付款用户：</span>
                            <span className={styles.summaryValue}>
                                {overviewStats.paidUsers78} 人
                            </span>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Divider />

            {/* 商品数量统计 */}
            <Card 
                title="商品数量统计" 
                className={styles.tableCard}
                extra={
                    <span 
                        onClick={() => setProductStatsVisible(!productStatsVisible)}
                        style={{ cursor: 'pointer', color: '#1890ff' }}
                    >
                        {productStatsVisible ? (
                            <Space>
                                <UpOutlined />
                                <Text>收起</Text>
                            </Space>
                        ) : (
                            <Space>
                                <DownOutlined />
                                <Text>展开</Text>
                            </Space>
                        )}
                    </span>
                }
            >
                {productStatsVisible && (
                    <Table
                        columns={[
                            {
                                title: '商品名称',
                                dataIndex: 'productName',
                                key: 'productName',
                                width: 250,
                                render: (text) => highlightText(text, searchKeyword),
                            },
                            {
                                title: '总数量',
                                dataIndex: 'totalQuantity',
                                key: 'totalQuantity',
                                width: 120,
                                align: 'center',
                                sorter: (a, b) => a.totalQuantity - b.totalQuantity,
                                defaultSortOrder: 'descend',
                                render: (quantity) => (
                                    <span style={{ fontWeight: 'bold', color: '#1890ff', fontSize: '16px' }}>
                                        {quantity}
                                    </span>
                                ),
                            },
                            {
                                title: '购买用户数',
                                dataIndex: 'userCount',
                                key: 'userCount',
                                width: 120,
                                align: 'center',
                                sorter: (a, b) => a.userCount - b.userCount,
                                render: (count) => `${count} 人`,
                            },
                            {
                                title: '购买用户',
                                dataIndex: 'users',
                                key: 'users',
                                render: (users: string[]) => (
                                    <Space wrap>
                                        {users.map((user: string, index: number) => (
                                            <Tag key={index} color="blue">{user}</Tag>
                                        ))}
                                    </Space>
                                ),
                            },
                        ]}
                        dataSource={productQuantityStats}
                        rowKey="productName"
                        pagination={{
                            pageSize: 20,
                            showSizeChanger: true,
                            showTotal: (total) => `共 ${total} 种商品`,
                        }}
                    />
                )}
            </Card>

            <Divider />

            {/* 视图切换开关 */}
            <Card className={styles.switchCard}>
                <div className={styles.switchContainer}>
                    <Space>
                        <AppstoreOutlined className={viewMode === 'card' ? styles.activeIcon : styles.inactiveIcon} />
                        <Switch
                            checked={viewMode === 'table'}
                            onChange={(checked) => setViewMode(checked ? 'table' : 'card')}
                            checkedChildren={<TableOutlined />}
                            unCheckedChildren={<AppstoreOutlined />}
                        />
                        <TableOutlined className={viewMode === 'table' ? styles.activeIcon : styles.inactiveIcon} />
                        <Text type="secondary">
                            {viewMode === 'table' ? '表格模式' : '卡片模式'}
                        </Text>
                    </Space>
                </div>
            </Card>

            {/* 用户订单展示 */}
            <Card title="用户订单明细" className={styles.tableCard}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Text>加载中...</Text>
                    </div>
                ) : viewMode === 'table' ? (
                    filteredData.length > 0 ? (
                        <Table
                            columns={userColumns}
                            dataSource={filteredData}
                            rowKey="userNickname"
                            expandable={{
                                expandedRowRender,
                                expandRowByClick: false,
                            }}
                            scroll={{ x: 800 }}
                            pagination={false}
                        />
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <Text type="secondary" style={{ fontSize: '16px' }}>
                                未找到匹配的用户或商品
                            </Text>
                        </div>
                    )
                ) : (
                    filteredData.length > 0 ? (
                        renderCardView()
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <Text type="secondary" style={{ fontSize: '16px' }}>
                                未找到匹配的用户或商品
                            </Text>
                        </div>
                    )
                )}
            </Card>
        </div>
    );
};

export default Goods;

